import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Link, Settings2 } from "lucide-react";
import { Dialog, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "../../Shared/Dialog";
import Button from "../../Shared/Buttons/Button";
import IconButton from "../../Shared/Buttons/IconButton";
import Input from "../../Shared/Input";
import Label from "../../Shared/Label";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import { dataService } from "../../../Services/data/data.service";
import { toast } from "react-toastify";

/**
 * @param {boolean}    open
 * @param {function}   onClose
 * @param {function}   onRegistered  - called after successful registration or configuration creation
 * @param {string}     userId
 * @param {Array}      companies     - [{id, name}, ...] companies the user belongs to
 */
function RegisterFlowBoxDeviceDialog({ open, onClose, onRegistered, userId, companies = [] }) {
  const { t } = useTranslation();

  // null = choice screen, "link" = register with IMEI, "prepare" = prepare config without device
  const [mode, setMode] = useState(null);

  // Shared
  const [displayName, setDisplayName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [companyError, setCompanyError] = useState("");

  // Link-device-only
  const [imei, setImei] = useState("");
  const [imeiError, setImeiError] = useState("");

  const multiCompany = companies.length > 1;

  useEffect(() => {
    if (!open) return;
    setMode(null);
    setSelectedCompany(companies.length === 1 ? companies[0] : null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setMode(null);
    setImei("");
    setDisplayName("");
    setSelectedCompany(null);
    setImeiError("");
    setNameError("");
    setCompanyError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmitLink = () => {
    let valid = true;
    if (!imei.trim()) { setImeiError(t("flowBox.register.imeiRequired")); valid = false; }
    if (!displayName.trim()) { setNameError(t("flowBox.register.displayNameRequired")); valid = false; }
    if (multiCompany && !selectedCompany) { setCompanyError(t("flowBox.register.companyRequired")); valid = false; }
    if (!valid) return;

    const companyId = multiCompany ? selectedCompany.id : (companies[0]?.id || null);
    setLoading(true);
    dataService
      .registerFlowBoxDevice({ imei: imei.trim(), displayName: displayName.trim(), companyId, insertedByUserId: userId })
      .then(() => {
        handleClose();
        toast.success(t("flowBox.register.registeredSuccess"));
        onRegistered();
      })
      .catch((err) => toast.error(err?.message || t("flowBox.register.registerFailed")))
      .finally(() => setLoading(false));
  };

  const handleSubmitPrepare = () => {
    let valid = true;
    if (!displayName.trim()) { setNameError(t("flowBox.register.displayNameRequired")); valid = false; }
    if (multiCompany && !selectedCompany) { setCompanyError(t("flowBox.register.companyRequired")); valid = false; }
    if (!valid) return;

    const companyId = multiCompany ? selectedCompany.id : (companies[0]?.id || null);
    setLoading(true);
    dataService
      .prepareFlowBoxConfiguration({ displayName: displayName.trim(), companyId, insertedByUserId: userId })
      .then(() => {
        handleClose();
        toast.success(t("flowBox.register.preparedSuccess"));
        onRegistered();
      })
      .catch((err) => toast.error(err?.message || t("flowBox.register.prepareFailed")))
      .finally(() => setLoading(false));
  };

  const companyField = multiCompany && (
    <div className="dialog-field">
      <Label errorText={companyError} required>{t("flowBox.register.company")}</Label>
      <Select
        value={selectedCompany?.id}
        onChange={(val) => {
          setSelectedCompany(companies.find((c) => c.id === val) || null);
          setCompanyError("");
        }}
        searchable
        fullwidth
      >
        <SelectTrigger error={!!companyError}>
          {selectedCompany ? selectedCompany.name : t("flowBox.register.selectCompany")}
        </SelectTrigger>
        <SelectContent>
          {companies.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const dialogTitle = mode === "link"
    ? t("flowBox.register.title")
    : mode === "prepare"
      ? t("flowBox.register.prepareConfigTitle")
      : t("flowBox.register.choiceTitle");

  const choiceCardStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "28px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#f9fafb",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    transition: "border-color 0.15s ease, background 0.15s ease",
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm">
      <DialogHeader closeable={mode === null}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {mode !== null && (
            <IconButton size="small" ariaLabel={t("common.back")} onClick={() => { setMode(null); setImeiError(""); setNameError(""); setCompanyError(""); }}>
              <ArrowLeft />
            </IconButton>
          )}
          <DialogTitle>{dialogTitle}</DialogTitle>
        </div>
      </DialogHeader>

      <DialogBody>
        {/* ── Choice screen ─────────────────────────────────────────────── */}
        {mode === null && (
          <div style={{ display: "flex", gap: 12 }}>
            <div
              role="button"
              tabIndex={0}
              style={choiceCardStyle}
              onClick={() => setMode("link")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setMode("link"); }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#f9fafb"; }}
            >
              <Link size={28} color="#6b7280" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Lato, sans-serif", marginBottom: 4 }}>
                  {t("flowBox.register.linkDevice")}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.4 }}>
                  {t("flowBox.register.linkDeviceDesc")}
                </div>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              style={choiceCardStyle}
              onClick={() => setMode("prepare")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setMode("prepare"); }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#f9fafb"; }}
            >
              <Settings2 size={28} color="#6b7280" />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Lato, sans-serif", marginBottom: 4 }}>
                  {t("flowBox.register.prepareConfig")}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.4 }}>
                  {t("flowBox.register.prepareConfigDesc")}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Link a physical device ─────────────────────────────────────── */}
        {mode === "link" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {companyField}
            <div className="dialog-field">
              <Label htmlFor="regImei" errorText={imeiError} required>{t("flowBox.register.imeiLabel")}</Label>
              <Input
                name="regImei"
                value={imei}
                onChange={(e) => { setImei(e.target.value); setImeiError(""); }}
                placeholder={t("flowBox.register.imeiPlaceholder")}
                error={!!imeiError}
                fullwidth
                inputProps={{ maxLength: 15 }}
              />
            </div>
            <div className="dialog-field">
              <Label htmlFor="regDisplayName" errorText={nameError} required>{t("flowBox.register.displayNameLabel")}</Label>
              <Input
                name="regDisplayName"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameError(""); }}
                placeholder={t("flowBox.register.displayNamePlaceholder")}
                error={!!nameError}
                fullwidth
                inputProps={{ maxLength: 100 }}
              />
            </div>
          </div>
        )}

        {/* ── Prepare configuration ──────────────────────────────────────── */}
        {mode === "prepare" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280", fontFamily: "Lato, sans-serif", lineHeight: 1.5 }}>
              {t("flowBox.register.prepareConfigHint")}
            </p>
            {companyField}
            <div className="dialog-field">
              <Label htmlFor="prepDisplayName" errorText={nameError} required>{t("flowBox.register.displayNameLabel")}</Label>
              <Input
                name="prepDisplayName"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameError(""); }}
                placeholder={t("flowBox.register.displayNamePlaceholder")}
                error={!!nameError}
                fullwidth
                inputProps={{ maxLength: 100 }}
              />
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="light" onClick={handleClose}>{t("common.cancel")}</Button>
        {mode === "link" && (
          <Button onClick={handleSubmitLink} disabled={loading} loading={loading}>
            {t("flowBox.register.register")}
          </Button>
        )}
        {mode === "prepare" && (
          <Button onClick={handleSubmitPrepare} disabled={loading} loading={loading}>
            {t("flowBox.register.prepare")}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

export default RegisterFlowBoxDeviceDialog;
